import { LuArrowUpDown, LuChevronDown, LuChevronUp } from "react-icons/lu";

const COLUMNS = [
  { key: "id", name: "ID", sort_value: "id", className: "w-[7%]" },
  { key: "receipt", name: "Kuponi", sort_value: "receipt_id", className: "w-[8%]" },
  { key: "game", name: "Loja", sort_value: "game_id", className: "w-[22%]" },
  { key: "business", name: "Biznesi", sort_value: "business_name", className: "w-[22%]" },
  { key: "user", name: "Përdoruesi", sort_value: "user_name", className: "w-[17%]" },
  { key: "prize", name: "Çmimi", sort_value: "prize_amount", className: "w-[12%]", align: "right" },
  { key: "created", name: "Krijuar", sort_value: "created_at", className: "w-[12%]" },
];

const TYPE_LABEL = {
  lottery: "Lottery",
  cashback: "Cashback",
  business_draw: "Business Draw",
};

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

const formatMoney = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  return `€${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB");
  } catch {
    return "—";
  }
};

const TypePill = ({ type }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-200">
    {TYPE_LABEL[type] || cap(type?.replace(/_/g, " ")) || "—"}
  </span>
);

const SkeletonRow = () => (
  <tr className="animate-pulse">
    <td className="px-6 py-4"><div className="h-3 w-8 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-3 w-10 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4">
      <div className="space-y-2">
        <div className="h-3 w-40 bg-gray-200 rounded" />
        <div className="h-2.5 w-20 bg-gray-100 rounded" />
      </div>
    </td>
    <td className="px-6 py-4">
      <div className="space-y-2">
        <div className="h-3 w-40 bg-gray-200 rounded" />
        <div className="h-2.5 w-24 bg-gray-100 rounded" />
      </div>
    </td>
    <td className="px-6 py-4"><div className="h-3 w-32 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-3 w-16 bg-gray-200 rounded ml-auto" /></td>
    <td className="px-6 py-4"><div className="h-3 w-20 bg-gray-200 rounded" /></td>
  </tr>
);

const SortIcon = ({ active, order }) => {
  if (!active)
    return (
      <LuArrowUpDown
        size={12}
        className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
      />
    );
  return order === "desc" ? (
    <LuChevronDown size={14} className="text-[var(--secondary-color)]" />
  ) : (
    <LuChevronUp size={14} className="text-[var(--secondary-color)]" />
  );
};

export default function BusinessWinnersTable({
  winners,
  isLoading,
  params = {},
  onSort,
  onRowClick,
  onGameClick,
  onReceiptClick,
}) {
  return (
    <div className="rounded-2xl border border-gray-200/80 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.03)] overflow-hidden">
      {/* Desktop */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              {COLUMNS.map((col) => {
                const sortable = !!col.sort_value;
                const active = sortable && params.sort_by === col.sort_value;
                return (
                  <th
                    key={col.key}
                    onClick={sortable ? () => onSort?.(col.sort_value) : undefined}
                    className={`group ${col.className} ${
                      col.align === "right" ? "text-right" : "text-left"
                    } px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider ${
                      active ? "text-[var(--secondary-color)]" : "text-gray-500"
                    } ${sortable ? "cursor-pointer select-none hover:text-gray-900" : ""} transition-colors`}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {col.name}
                      {sortable && <SortIcon active={active} order={params.sort_order} />}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (!winners || winners.length === 0)
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
              : winners?.map((w) => (
                  <tr
                    key={w.business_winner_id}
                    onClick={() => onRowClick?.(w)}
                    className="group cursor-pointer transition-colors hover:bg-gray-50/70"
                  >
                    <td className="px-6 py-3.5 text-gray-500 tabular-nums text-xs">
                      #{w.business_winner_id}
                    </td>
                    <td className="px-6 py-3.5 tabular-nums text-xs">
                      {w.receipt_id ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onReceiptClick?.(w);
                          }}
                          className="text-gray-700 hover:text-[var(--secondary-color)] hover:underline"
                        >
                          #{w.receipt_id}
                        </button>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-3.5">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onGameClick?.(w);
                        }}
                        className="text-left font-medium text-gray-900 hover:text-[var(--secondary-color)] hover:underline truncate max-w-full"
                      >
                        {w.game_title || `Loja #${w.game_id}`}
                      </button>
                      <div className="mt-0.5">
                        <TypePill type={w.game_type} />
                      </div>
                    </td>
                    <td className="px-6 py-3.5">
                      <div className="font-medium text-gray-900 truncate">
                        {w.business_name || "—"}
                      </div>
                      <div className="text-xs text-gray-500 tabular-nums truncate">
                        {w.business_nui ? `NUI ${w.business_nui}` : ""}
                        {w.business_city ? ` · ${w.business_city}` : ""}
                      </div>
                    </td>
                    <td className="px-6 py-3.5">
                      <div className="text-gray-700 truncate">{w.user_name || "—"}</div>
                      {w.user_phone && (
                        <div className="text-xs text-gray-500 tabular-nums">{w.user_phone}</div>
                      )}
                    </td>
                    <td className="px-6 py-3.5 text-right text-gray-900 font-semibold tabular-nums">
                      <span className="line-clamp-1">{w.prize_description}</span>
                    </td>
                    <td className="px-6 py-3.5 text-gray-600 whitespace-nowrap tabular-nums text-xs">
                      {formatDate(w.created_at)}
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="md:hidden divide-y divide-gray-100">
        {isLoading && (!winners || winners.length === 0) ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="animate-pulse p-3 rounded-xl bg-gray-50 space-y-2">
                <div className="h-3 w-1/2 bg-gray-200 rounded" />
                <div className="h-2.5 w-3/4 bg-gray-100 rounded" />
              </div>
            ))}
          </div>
        ) : (
          winners?.map((w) => (
            <button
              key={w.business_winner_id}
              type="button"
              onClick={() => onRowClick?.(w)}
              className="w-full text-left p-4 hover:bg-gray-50 active:bg-gray-100 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 truncate">
                    {w.business_name || "—"}
                  </div>
                  <div className="text-xs text-gray-500 truncate tabular-nums">
                    {w.business_nui ? `NUI ${w.business_nui}` : ""}
                    {w.business_city ? ` · ${w.business_city}` : ""}
                  </div>
                </div>
                <span className="text-gray-900 font-semibold tabular-nums whitespace-nowrap">
                  {formatMoney(w.prize_amount)}
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs text-gray-700 truncate">
                    {w.game_title || `Loja #${w.game_id}`}
                  </div>
                  <div className="mt-0.5">
                    <TypePill type={w.game_type} />
                  </div>
                </div>
                <span className="text-xs text-gray-500 tabular-nums whitespace-nowrap">
                  {w.receipt_id && (
                    <>
                      <span
                        role="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          onReceiptClick?.(w);
                        }}
                        className="hover:text-[var(--secondary-color)] hover:underline"
                      >
                        Kuponi #{w.receipt_id}
                      </span>
                      {" · "}
                    </>
                  )}
                  {formatDate(w.created_at)}
                </span>
              </div>
              {w.user_name && (
                <div className="mt-2 text-xs text-gray-500 truncate">{w.user_name}</div>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
